import { ENotAuthNavigation } from '@hts/shared/enums';
import { IMappedSubmenuCategory } from '@hts/shared/types';
import { HtsButtonIcon, HtslIcon, HtsLink } from '@hts/shared/ui';
import { domAnimation, LazyMotion, m } from 'framer-motion';
import { memo, useCallback, useMemo, useState } from 'react';
import styles from '../../../styles/Header/Mobile/ExpandedCategory.module.scss';

interface IExpandedCategoryProps {
  category: IMappedSubmenuCategory;
  onClose: () => void;
}

export const ExpandedCategory: React.FC<IExpandedCategoryProps> = memo(({ category, onClose }) => {
  const { categories, name, slug: parentSlug } = category;

  const [expanded, setExpanded] = useState(false);

  const parentLinkTo = `${ENotAuthNavigation.CATEGORY_OF_CARD}/${parentSlug}`;

  const toggleExpanded = useCallback(() => setExpanded((prev) => !prev), []);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleExpanded();
    }
  }, []);

  const getRedirectLinkToChild = useCallback(
    (slug: string) => `${parentLinkTo}?sub=${slug}`,
    [parentLinkTo]
  );

  const containerAttributes = useMemo<React.HTMLAttributes<HTMLDivElement>>(() => {
    if (!categories.length) {
      return {
        role: undefined,
        tabIndex: undefined,
        'aria-expanded': undefined,
        'aria-label': undefined,
        onKeyDown: undefined,
        onClick: undefined,
      };
    }

    return {
      role: 'button',
      tabIndex: 0,
      'aria-expanded': expanded,
      'aria-label': expanded ? 'Collapse category' : 'Expand category',
      onKeyDown,
      onClick: toggleExpanded,
    };
  }, [categories.length, expanded, onKeyDown, toggleExpanded]);

  const renderChild = useCallback(
    ({ id, name, slug }: IMappedSubmenuCategory, index: number) => (
      <li key={`${id}-${index}`}>
        <HtsLink
          label={name}
          redirectTo={getRedirectLinkToChild(slug)}
          typographyProps={{ variant: 'subtitle', fontWeight: '400' }}
          linkProps={{ onClick: onClose }}
        />
      </li>
    ),
    [getRedirectLinkToChild, onClose]
  );

  return (
    <li className={styles.container}>
      <div className={styles.expandedLabelContainer} {...containerAttributes}>
        <HtsLink
          isConditionalLink={Boolean(categories.length)}
          label={name}
          redirectTo={parentLinkTo}
          typographyProps={{ variant: 'body2', fontWeight: '500' }}
          linkProps={{ onClick: onClose }}
        />

        {Boolean(categories.length) && (
          <HtsButtonIcon
            style={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(90deg)',
              transition: 'transform 0.3s',
            }}
          >
            <HtslIcon icon={expanded ? 'minus' : 'plus'} />
          </HtsButtonIcon>
        )}
      </div>

      <LazyMotion features={domAnimation} strict>
        <div aria-expanded={expanded}>
          {categories.length > 0 && (
            <m.ul
              style={{ overflow: 'hidden' }}
              initial={{ height: 0, opacity: 1, marginTop: 0 }}
              exit={{ height: 0, opacity: 1, marginTop: 0 }}
              animate={{
                transition: { type: 'tween' },
                height: expanded ? 'auto' : 0,
                marginTop: expanded ? 20 : 0,
              }}
            >
              {categories.map(renderChild)}
            </m.ul>
          )}
        </div>
      </LazyMotion>
    </li>
  );
});
